/**
 * Client for the in-app reports endpoints (wig-broker-deals#239, de-hub#319).
 *
 *   GET    /api/broker_deals/reports/catalog   datasets, fields, measures, limits
 *   POST   /api/broker_deals/reports/query     run one declarative query
 *   GET    /api/broker_deals/reports           published + the caller's drafts
 *   GET    /api/broker_deals/reports/{id}      one report, re-validated
 *   POST   /api/broker_deals/reports           create        (BD.Admin)
 *   PUT    /api/broker_deals/reports/{id}      partial update (BD.Admin)
 *   DELETE /api/broker_deals/reports/{id}      delete        (BD.Admin)
 *
 *   SQL report sources (BD.Admin; the Report sources page):
 *   GET    /api/broker_deals/reports/sources          list, all statuses
 *   GET    /api/broker_deals/reports/sources/{id}     one source
 *   POST   /api/broker_deals/reports/sources          create
 *   PUT    /api/broker_deals/reports/sources/{id}     partial update
 *   DELETE /api/broker_deals/reports/sources/{id}     delete
 *   POST   /api/broker_deals/reports/sources/preview  run `{sql}`, nothing saved
 *
 * A report is a DECLARATIVE spec, never SQL (an admin-defined SQL source
 * becomes a dataset `src_<key>`; reports still only query it declaratively). The server validates every query
 * against the caller's field catalog and scopes rows exactly as the dataset's
 * list endpoint does, so nothing here filters or hides anything client-side.
 *
 * Each method takes `{ token, userOid, ... }` and returns the raw axios
 * response; callers read `resp.data.data` per the standard de-hub envelope.
 */
import dataService from "./dataservice";

const BASE = "/broker_deals/reports";

const reportPath = (id) => `${BASE}/${encodeURIComponent(id)}`;

/** GET /reports/catalog — `{datasets, measures, buckets, limits}`. */
const getCatalog = ({ token, userOid }) =>
  dataService.getEndpoint(`${BASE}/catalog`, token, userOid);

/**
 * POST /reports/query — `{columns, rows, truncated}`.
 *
 * `query` is a widget's query spec (`{dataset, filters?, group_by?, measures,
 * sort?, limit?}`), sent as-is. A spec the catalog rejects is a 422 whose
 * `detail` is `[{loc, msg, type, field}]`.
 */
const runQuery = ({ token, userOid, query }) =>
  dataService.searchEndpoint(`${BASE}/query`, token, userOid, query);

/** GET /reports — summary rows (no spec), ordered by name. */
const listReports = ({ token, userOid }) =>
  dataService.getEndpoint(BASE, token, userOid);

/**
 * GET /reports/{id} — summary + `spec` + `valid` + `validation`
 * (`[{widget_id, index, issues}]`). 404 covers both a missing id and someone
 * else's draft.
 */
const getReport = ({ token, userOid, id }) =>
  dataService.getEndpoint(reportPath(id), token, userOid);

/** POST /reports — `body` is `{name, description?, status?, spec}`. */
const createReport = ({ token, userOid, body }) =>
  dataService.postEndpoint(BASE, token, userOid, body);

/**
 * PUT /reports/{id} — partial: only the keys present in `body` are written.
 * Send `{status: "draft"}` alone to unpublish.
 */
const updateReport = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(reportPath(id), token, userOid, body);

/** DELETE /reports/{id}. */
const deleteReport = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(reportPath(id), token, userOid);

/**
 * POST /reports/{id}/clone — copies the report (as a draft in the Preview
 * category, owned by the caller) and every SQL source it reads; returns the
 * new report's summary + spec, `cloned_from` and `sources` (the copies).
 */
const cloneReport = ({ token, userOid, id }) =>
  dataService.postEndpoint(`${reportPath(id)}/clone`, token, userOid, {});

/*
 * SQL report sources. Like the calls above they reject with the raw axios
 * error: the library's WigReportSources reads `response.status` and
 * `response.data.detail` (422: the SQL / Postgres message; 409: the key
 * exists, or `detail.reports` lists saved reports a new SQL would break).
 */
const SOURCES = `${BASE}/sources`;

const sourcePath = (id) => `${SOURCES}/${encodeURIComponent(id)}`;

/** GET /reports/sources — every source, all statuses, ordered by name. */
const listSources = ({ token, userOid }) =>
  dataService.getEndpoint(SOURCES, token, userOid);

/** GET /reports/sources/{id} — one source. */
const getSource = ({ token, userOid, id }) =>
  dataService.getEndpoint(sourcePath(id), token, userOid);

/** POST /reports/sources — `body` is `{key, name, description?, sql, status?, columns?}`. */
const createSource = ({ token, userOid, body }) =>
  dataService.postEndpoint(SOURCES, token, userOid, body);

/** PUT /reports/sources/{id} — partial; `key` is not editable. */
const updateSource = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(sourcePath(id), token, userOid, body);

/** DELETE /reports/sources/{id}. */
const deleteSource = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(sourcePath(id), token, userOid);

/** POST /reports/sources/preview — `{sql}`: first rows + discovered columns. */
const previewSource = ({ token, userOid, sql }) =>
  dataService.postEndpoint(`${SOURCES}/preview`, token, userOid, { sql });

const reportsService = {
  getCatalog,
  runQuery,
  listReports,
  getReport,
  createReport,
  updateReport,
  deleteReport,
  cloneReport,
  listSources,
  getSource,
  createSource,
  updateSource,
  deleteSource,
  previewSource,
};

export default reportsService;
